<template>
    <b-modal
        :visible="true"
        size="xl"
        scrollable
        title="평가 프로파일 편집"
        @hidden="$emit('close')"
    >
        <div v-if="loading" class="text-center py-5">
            <b-spinner /> 불러오는 중...
        </div>

        <div v-else>
            <!-- 메타 -->
            <b-row class="mb-3">
                <b-col md="6">
                    <b-form-group label="프로파일 이름">
                        <b-form-input v-model="profileName" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="상태">
                        <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="질문 버전">
                        <span class="text-muted">{{ questionSetVersion }}</span>
                    </b-form-group>
                </b-col>
            </b-row>

            <!-- 프로젝트 연결 -->
            <b-card no-body class="p-3 mb-4 bg-light">
                <label class="font-weight-bold mb-2">프로젝트에 연결 (기본 평가 기준으로 적용)</label>
                <div class="position-relative">
                    <b-form-input
                        v-model="projectSearch"
                        placeholder="프로젝트 코드 또는 클라이언트명으로 검색"
                        autocomplete="off"
                        @input="onProjectSearchInput"
                        @focus="onProjectSearchFocus"
                        @click="onProjectSearchFocus"
                        @blur="onProjectSearchBlur"
                    />
                    <b-list-group
                        v-show="projectSearchFocused && projectResults.length"
                        class="position-absolute w-100 shadow-sm project-search-list"
                    >
                        <b-list-group-item
                            v-for="p in projectResults"
                            :key="p.project_code"
                            button
                            :class="['px-3', 'py-2', { 'project-item-linked': isLinked(p) }]"
                            @mousedown.prevent="selectProject(p)"
                        >
                            <div class="d-flex justify-content-between align-items-center">
                                <span>{{ p.label || p.project_code }}</span>
                                <b-badge v-if="isLinked(p)" variant="info">연결됨</b-badge>
                            </div>
                        </b-list-group-item>
                    </b-list-group>
                </div>
                <small v-if="projectSearching" class="text-muted mt-1">검색 중...</small>
                <small
                    v-else-if="projectSearchFocused && projectTotal > projectResults.length"
                    class="text-warning mt-1"
                >
                    상위 {{ projectResults.length }}개만 표시됩니다 (검색 결과 {{ projectTotal }}개) — 검색어를 더 좁혀주세요.
                </small>
                <small class="text-muted mt-1">
                    프로젝트 코드나 클라이언트명으로 검색해 선택하면 해당 프로젝트의 기본 평가 기준으로 연결됩니다.
                    운영(active) 상태의 프로파일을 연결하는 것을 권장하며, 프로젝트당 기본 프로파일은 하나만 적용됩니다.
                    연결/해제는 아래 [저장]을 눌러야 실제로 반영됩니다.
                </small>
                <div v-if="linkedProjects.length" class="mt-2">
                    <b-badge
                        v-for="m in linkedProjects"
                        :key="m.project_code"
                        variant="info"
                        class="mr-2 mb-1"
                    >
                        {{ m.label || m.project_code }}
                        <span v-if="m.is_default">(기본)</span>
                        <b-link class="text-white ml-1" @click="unlinkProject(m)">×</b-link>
                    </b-badge>
                </div>
            </b-card>

            <!-- 자율형식 평가 기준 -->
            <div class="question-list">
                <div
                    v-for="(q) in questions"
                    :key="q.question_id"
                    :ref="`question-card-${q.question_id}`"
                    class="question-card mb-4"
                >
                    <div class="d-flex justify-content-between align-items-start">
                        <label class="font-weight-bold mb-1">
                            {{ q.title }}
                        </label>
                        <div class="d-flex align-items-center flex-shrink-0 ml-2">
                            <b-button
                                v-if="isDraftableQuestion(q)"
                                size="sm"
                                variant="outline-primary"
                                class="mr-2"
                                @click="openDraftModal(q.question_id)"
                            >
                                자료로 기준 초안 만들기
                            </b-button>
                            <b-badge v-if="!isAnswered(q.question_id)" variant="light">
                                미작성 — 기본 기준 적용
                            </b-badge>
                        </div>
                    </div>
                    <p class="text-muted small mb-2" style="white-space: pre-line">{{ q.situation }}</p>
                    <b-form-textarea
                        v-model="answers[q.question_id]"
                        :placeholder="q.placeholder || '평가 기준을 자유롭게 작성하세요'"
                        :rows="8"
                        max-rows="20"
                    />
                    <b-alert
                        v-if="isDraftApplied(q.question_id)"
                        show
                        variant="info"
                        dismissible
                        class="mt-2 mb-0"
                        @dismissed="draftApplied = null"
                    >
                        <div class="font-weight-bold">
                            AI 초안이 채워졌습니다 — 검토·수정 후 [저장]을 눌러야 반영됩니다.
                        </div>
                        <div class="small mt-1">
                            <div class="mb-1">이 초안의 참조 자료:</div>
                            <DraftSourcesList :sources="draftApplied.sources" />
                        </div>
                    </b-alert>
                </div>
            </div>

            <EvalProfileDraftModal
                v-if="draftModalOpen"
                :profile-id="profileId"
                @done="onDraftDone"
                @apply="onDraftDone"
                @close="draftModalOpen = false"
            />
        </div>

        <template #modal-footer>
            <span class="text-muted mr-auto small">
                작성하지 않은 항목은 시스템 기본 기준이 적용됩니다.
            </span>
            <b-button variant="secondary" @click="$emit('close')">닫기</b-button>
            <b-button variant="primary" :disabled="saving" @click="saveAnswers">
                <b-spinner v-if="saving" small /> 저장
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import EvalProfileDraftModal from './EvalProfileDraftModal.vue';
import DraftSourcesList from './DraftSourcesList.vue';

// 백엔드 draft-context API가 초안을 생성하는 대상 질문 (질문셋 v2.0.0의 자유 서술 질문).
const DRAFTABLE_QUESTION_ID = 'freeform_context';
// 프로젝트 검색 debounce (ms) — 서버사이드 검색이라 키 입력마다 치지 않는다.
const PROJECT_SEARCH_DEBOUNCE_MS = 250;

export default {
    name: 'EvalProfileForm',
    components: { EvalProfileDraftModal, DraftSourcesList },
    props: {
        profileId: { type: Number, required: true },
    },
    data() {
        return {
            loading: true,
            saving: false,
            profileName: '',
            status: 'draft',
            questionSetVersion: '',
            questions: [],
            answers: {},
            // 연결된 프로젝트 매핑 [{id, project_code, is_default, label}] (로컬 편집 상태)
            linkedProjects: [],
            // 저장 시 추가/삭제 diff 계산용 서버 스냅샷 [{id, project_code}]
            originalMappings: [],
            // 프로젝트 검색(서버사이드)
            projectSearch: '',
            projectResults: [],
            projectTotal: 0,
            projectSearching: false,
            projectSearchFocused: false,
            projectSearchTimer: null,
            draftModalOpen: false,
            draftTargetQid: null,
            draftApplied: null, // { questionId, sources } — 초안 적용 후 안내용
        };
    },
    computed: {
        statusLabel() {
            return { draft: '초안', active: '운영', archived: '보관' }[this.status] || this.status;
        },
        statusVariant() {
            return { draft: 'secondary', active: 'success', archived: 'light' }[this.status] || 'secondary';
        },
        // 현재 연결된 project_code 집합 (드롭다운에서 '연결됨' 표시용)
        linkedProjectCodeSet() {
            return new Set(this.linkedProjects.map((m) => m.project_code));
        },
    },
    async created() {
        await this.loadAll();
    },
    beforeDestroy() {
        if (this.projectSearchTimer) clearTimeout(this.projectSearchTimer);
    },
    methods: {
        async loadAll() {
            this.loading = true;
            try {
                const [qsRes, profRes] = await Promise.all([
                    this.$axios.get('/eval-profiles/question-set'),
                    this.$axios.get(`/eval-profiles/${this.profileId}`),
                ]);
                this.questions = qsRes.data.questions;
                this.questionSetVersion = qsRes.data.version;

                const p = profRes.data;
                this.profileName = p.profile_name;
                this.status = p.status;
                // 모든 질문 id에 대해 빈 문자열 초기화 후 기존 답변 덮어쓰기
                const init = {};
                this.questions.forEach((q) => { init[q.question_id] = ''; });
                Object.entries(p.answers || {}).forEach(([k, v]) => { init[k] = v; });
                this.answers = init;

                await this.loadLinkedProjects();
            } catch (e) {
                this.$bvToast.toast('프로파일을 불러오지 못했습니다.', {
                    title: '오류', variant: 'danger',
                });
            } finally {
                this.loading = false;
            }
        },
        async loadLinkedProjects() {
            // 이 프로파일이 연결된 프로젝트 목록을 역조회해 표시한다.
            // 폼 재진입 시에도 이미 연결된 프로젝트가 보이도록 한다.
            try {
                const { data } = await this.$axios.get(
                    `/eval-profiles/${this.profileId}/projects`,
                );
                this.linkedProjects = data;
                // 저장 시 추가/삭제 diff 계산을 위한 서버 스냅샷
                this.originalMappings = data.map((m) => ({
                    id: m.id, project_code: m.project_code,
                }));
            } catch (e) {
                // 역조회 실패는 치명적이지 않다(연결 자체는 동작). 빈 목록으로 둔다.
                this.linkedProjects = [];
                this.originalMappings = [];
            }
        },
        // 검색어 입력마다 debounce 후 서버 검색을 친다(질문지 피커의 전체 로드 문제 회피).
        onProjectSearchInput() {
            // 항목 선택 후 selectProject 가 focused 를 false 로 닫아도, 같은 input 에
            // 이어서 타이핑하면 @focus 가 다시 발화하지 않아 드롭다운이 안 뜬다.
            // 입력이 들어오면 다시 열어 재검색 결과가 보이도록 한다.
            this.projectSearchFocused = true;
            if (this.projectSearchTimer) clearTimeout(this.projectSearchTimer);
            this.projectSearchTimer = setTimeout(() => {
                this.searchProjects();
            }, PROJECT_SEARCH_DEBOUNCE_MS);
        },
        onProjectSearchFocus() {
            this.projectSearchFocused = true;
            // 포커스 시 결과가 없으면 초기 목록(상위 N개)을 한 번 받아온다.
            if (!this.projectResults.length && !this.projectSearching) {
                this.searchProjects();
            }
        },
        // 드롭다운 항목 클릭(mousedown)이 blur보다 먼저 처리되도록 blur를 지연시킨다.
        onProjectSearchBlur() {
            setTimeout(() => { this.projectSearchFocused = false; }, 150);
        },
        async searchProjects() {
            const q = this.projectSearch.trim();
            this.projectSearching = true;
            try {
                const { data } = await this.$axios.get('/eval-profiles/projects', {
                    params: { q, limit: 30 },
                });
                // linked 를 여기서 정적으로 박지 않는다 — 배지에서 unlink 하면
                // linkedProjects 가 바뀌므로, 렌더 시점에 isLinked()(computed 기반)로
                // 판단해야 드롭다운의 '연결됨' 표시가 실시간 반영된다.
                this.projectResults = data.projects || [];
                this.projectTotal = data.total || this.projectResults.length;
            } catch (e) {
                // 검색 실패는 치명적이지 않다. 빈 목록으로 둔다.
                this.projectResults = [];
                this.projectTotal = 0;
            } finally {
                this.projectSearching = false;
            }
        },
        // 렌더 시점에 실시간으로 연결 여부 판단 (linkedProjectCodeSet computed 기반).
        // projectResults 에 linked 를 박으면 unlink 후 재계산이 안 돼 '연결됨'이 남는다.
        isLinked(p) {
            return this.linkedProjectCodeSet.has(p.project_code);
        },
        // 검색 결과에서 프로젝트 선택 -> 로컬 연결 목록에 추가(저장 시 반영).
        selectProject(p) {
            if (!p || this.isLinked(p)) return;
            if (!this.linkedProjects.some((m) => m.project_code === p.project_code)) {
                this.linkedProjects.push({
                    id: null, // 신규(저장 전) — 저장 시 POST
                    project_code: p.project_code,
                    is_default: true,
                    label: p.label || p.project_code,
                });
            }
            this.projectSearch = '';
            this.projectResults = [];
            this.projectSearchFocused = false;
        },
        // 로컬 연결 목록에서만 제거(저장 시 실제 삭제 반영).
        unlinkProject(m) {
            this.linkedProjects = this.linkedProjects.filter(
                (x) => x.project_code !== m.project_code,
            );
        },
        // 로컬 편집된 연결 목록을 서버 스냅샷과 비교해 추가/삭제만 API로 반영한다.
        async applyProjectMappingDiff() {
            const currentCodes = new Set(this.linkedProjects.map((m) => m.project_code));
            const originalCodes = new Set(this.originalMappings.map((m) => m.project_code));
            const toAdd = this.linkedProjects.filter(
                (m) => !originalCodes.has(m.project_code),
            );
            const toRemove = this.originalMappings.filter(
                (m) => !currentCodes.has(m.project_code),
            );
            if (!toAdd.length && !toRemove.length) return;
            await Promise.all([
                ...toAdd.map((m) => this.$axios.post(
                    `/eval-profiles/project/${encodeURIComponent(m.project_code)}/mapping`,
                    { profile_id: this.profileId, is_default: true },
                )),
                ...toRemove.map((m) => this.$axios.delete(
                    `/eval-profiles/project/${encodeURIComponent(m.project_code)}/mapping/${m.id}`,
                )),
            ]);
            // 서버 id 확보 + linked 표시 재동기화
            await this.loadLinkedProjects();
        },
        isAnswered(qid) {
            return !!(this.answers[qid] && this.answers[qid].trim());
        },
        // 초안 생성 버튼은 자유 서술(freeform) 질문에만 노출한다.
        // 백엔드 draft-context API가 이 질문의 초안만 생성한다 (이슈 #140).
        isDraftableQuestion(q) {
            return q.question_id === DRAFTABLE_QUESTION_ID;
        },
        isDraftApplied(qid) {
            return !!(this.draftApplied && this.draftApplied.questionId === qid);
        },
        openDraftModal(qid) {
            this.draftTargetQid = qid;
            this.draftModalOpen = true;
        },
        // 초안 완료(자동) 또는 [채워 넣기] 클릭(수동) 시 textarea에 채운다.
        // 저장은 절대 하지 않는다 — 확정은 기존 저장 버튼(PUT /answers)만.
        async onDraftDone({ draft, sources }) {
            const qid = this.draftTargetQid;
            if (!qid) return;
            const current = (this.answers[qid] || '').trim();
            if (current && current !== (draft || '').trim()) {
                const ok = await this.$bvModal.msgBoxConfirm(
                    '이미 작성된 평가 기준이 있습니다. AI 초안으로 교체할까요? 교체해도 저장 전까지는 반영되지 않습니다.',
                    {
                        title: '초안으로 교체',
                        okTitle: '교체',
                        cancelTitle: '유지',
                        okVariant: 'primary',
                    },
                );
                // 유지를 선택하면 초안 창의 [채워 넣기] 버튼으로 나중에 적용할 수 있다.
                if (!ok) return;
            }
            this.$set(this.answers, qid, draft);
            this.draftApplied = { questionId: qid, sources: sources || [] };
            this.draftModalOpen = false;
            // 스크롤 위치가 다른 곳이면 채워진 결과가 안 보이므로 해당 질문 카드로 이동.
            this.$nextTick(() => {
                const refs = this.$refs[`question-card-${qid}`];
                const el = Array.isArray(refs) ? refs[0] : refs;
                if (el && el.scrollIntoView) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
        async saveAnswers() {
            this.saving = true;
            try {
                // 이름 변경 반영
                await this.$axios.patch(`/eval-profiles/${this.profileId}`, {
                    profile_name: this.profileName,
                });
                // 답변 저장(빈 답변은 백엔드에서 미답 처리)
                await this.$axios.put(`/eval-profiles/${this.profileId}/answers`, {
                    answers: this.answers,
                });
                // 프로젝트 연결/해제 diff 를 저장 시점에 일괄 반영
                await this.applyProjectMappingDiff();
                this.$bvToast.toast('저장되었습니다.', { title: '완료', variant: 'success' });
                // 저장이 끝났으므로 "저장을 눌러야 반영됩니다" 초안 안내는 내린다.
                this.draftApplied = null;
                this.$emit('saved');
            } catch (e) {
                const msg = getErrorMessage(e, '저장에 실패했습니다.');
                this.$bvToast.toast(msg, { title: '오류', variant: 'danger' });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.question-card {
    border-left: 3px solid #e9ecef;
    padding-left: 12px;
}
.project-search-list {
    z-index: 1051; /* b-modal 내부에서도 위로 뜨도록 */
    max-height: 260px;
    overflow-y: auto;
}
/* 이미 연결된 항목: 흐리게 + 클릭해도 무반응(selectProject early return).
   disabled 를 쓰면 mousedown.prevent 가 안 걸려 blur→드롭다운이 닫히므로 클래스로만 처리. */
.project-item-linked {
    color: #adb5bd;
    cursor: default;
}
</style>
